"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { UpsellModal, type UpsellPrice } from "./upsell-modal";
import { clientTrack } from "@/lib/analytics/client";
import type { Track } from "@/lib/analytics/track";
import { emitPaywallHit, emitPaywallUpgradeClicked, quotaLimitToQuotaType, quotaLimitToTrigger } from "@/lib/analytics/paywall";
import { webAppContext } from "@/lib/analytics/surface";
import type { AnalyticsContext } from "@/lib/analytics/envelope";
import type { QuotaLimit } from "@/lib/billing/quota";
import { planDisplayName } from "@/lib/billing/paywallPresentation";

/**
 * The instrumented paywall (M12-B, §6 Stage E). It reconciles with the
 * presentational `UpsellModal` (M7 FR-Q5) — reusing its named-limit +
 * transparent-price + single-Upgrade-CTA, dark-pattern-free surface — and adds
 * the canonical conversion instrumentation:
 *
 *   on open  → paywall_hit { trigger, quota_type, usage }   (the §6 required shape)
 *   on CTA   → upgrade_clicked { trigger, plan_code, checkout_attempt_id },
 *              then the M7 checkout request (onCheckout). The route emits
 *              checkout_started only after Stripe creates a usable session.
 *
 * VARIANT SEAM: `copyVariant` renders the paywall-copy experiment's assigned
 * variant (control vs urgency copy). The variant + its exposure are resolved
 * SERVER-SIDE (see `resolveVariantWithExposure`) at the gate boundary; this
 * component only renders the assigned copy. No dark pattern, and the cancel
 * path is never wrapped or A/B-tested.
 */

export type PaywallCopyVariant = "control" | "urgency";

export interface PaywallProps {
  open: boolean;
  onClose: () => void;
  /** The specific limit the block is attributable to. */
  limit: QuotaLimit;
  /** White-label brand name — from getTheme, never a literal. */
  brandName: string;
  /** White-label brand mark (brand.logoUrl) shown in the modal glyph. */
  logoUrl?: string;
  /** Transparent price from the tunable plan table; null hides the price line. */
  price: UpsellPrice | null;
  /** The paid plan code checkout targets. */
  planCode: string;
  /** Configured per-map allowance for the recommended plan; null means unlimited. */
  planMapPointLimit?: number | null;
  /** Current usage at the block, for the canonical paywall_hit shape. */
  usage: number;
  /** Runs checkout after the CTA intent emits, carrying its correlation id. */
  onCheckout: (checkoutAttemptId: string, email?: string) => void | Promise<void>;
  /** Anonymous visitors must identify before Clerk resumes checkout. */
  requireEmail?: boolean;
  /** Sign-in path that preserves the current draft. */
  signInHref?: string;
  /** Checkout redirect is in flight. */
  upgrading?: boolean;
  /** Full pricing page (default "/pricing"). */
  pricingHref?: string;
  /** The assigned paywall-copy variant (from the server-resolved seam). */
  copyVariant?: PaywallCopyVariant;
  /** Session ids used to build the client analytics context. */
  anonSessionId?: string;
  userId?: string;
  /** Emission seam (defaults to the browser sink); tests inject a mock. */
  track?: Track;
  /** Analytics context override (defaults to a web_app context from the ids). */
  ctx?: AnalyticsContext;
}

/** The urgency variant's emphasis line — honest value framing, no fake scarcity. */
function emphasisFor(limit: QuotaLimit, variant: PaywallCopyVariant): string | undefined {
  if (variant !== "urgency") return undefined;
  switch (limit) {
    case "saved_maps":
      return "Upgrade now to continue making and sharing amazing maps.";
    case "watermark":
      return "Upgrade now to ship clean, unbranded embeds on every page you publish.";
    case "live_maps":
    case "live_sync":
    case "crm_sync":
      return "Upgrade now to keep your maps live and in sync with the data behind them.";
    case "refresh_cadence":
    case "scheduled_refresh":
      return "Upgrade now to let your maps refresh automatically, so they’re never stale.";
    case "api_access":
      return "Upgrade now to build on your maps and datasets programmatically.";
    case "private_share":
      return "Upgrade now to keep your maps private or unlisted, off the public directory.";
    case "map_points":
      return "Upgrade now to map more locations and keep the full dataset together.";
  }
}

export function Paywall({
  open,
  onClose,
  limit,
  brandName,
  logoUrl,
  price,
  planCode,
  planMapPointLimit,
  usage,
  onCheckout,
  requireEmail = false,
  signInHref,
  upgrading = false,
  pricingHref = "/pricing",
  copyVariant = "control",
  anonSessionId,
  userId,
  track,
  ctx,
}: PaywallProps) {
  const [email, setEmail] = useState("");
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const resolvedTrack = useMemo<Track>(() => track ?? clientTrack, [track]);
  const resolvedCtx = useMemo<AnalyticsContext>(
    () => ctx ?? webAppContext({ anonSessionId, userId }),
    [ctx, anonSessionId, userId],
  );
  const trigger = quotaLimitToTrigger(limit);

  // Fire the canonical paywall_hit exactly once when the gate is shown.
  const hitFired = useRef(false);
  useEffect(() => {
    if (!open || hitFired.current) return;
    hitFired.current = true;
    emitPaywallHit(resolvedTrack, resolvedCtx, {
      trigger,
      quotaType: quotaLimitToQuotaType(limit),
      usage,
      planCode,
    });
  }, [open, resolvedTrack, resolvedCtx, trigger, limit, usage, planCode]);

  async function handleUpgrade() {
    const normalizedEmail = email.trim().toLowerCase();
    if (requireEmail && !normalizedEmail) {
      setCaptureError("Enter your email to continue.");
      return;
    }
    const checkoutAttemptId = crypto.randomUUID();
    emitPaywallUpgradeClicked(resolvedTrack, resolvedCtx, { trigger, planCode, checkoutAttemptId });
    setCaptureError(null);
    setCapturing(true);
    try {
      await onCheckout(checkoutAttemptId, requireEmail ? normalizedEmail : undefined);
    } catch (err) {
      setCaptureError(err instanceof Error ? err.message : "We couldn't continue. Try again.");
      setCapturing(false);
    }
  }

  return (
    <UpsellModal
      open={open}
      onClose={onClose}
      limit={limit}
      brandName={brandName}
      logoUrl={logoUrl}
      price={price}
      planName={planDisplayName(planCode)}
      planMapPointLimit={planMapPointLimit}
      onUpgrade={handleUpgrade}
      upgrading={upgrading || capturing}
      pricingHref={pricingHref}
      emphasis={emphasisFor(limit, copyVariant)}
      emailCapture={
        requireEmail
          ? {
              value: email,
              onChange: (value) => {
                setEmail(value);
                if (captureError) setCaptureError(null);
              },
              error: captureError,
              signInHref,
            }
          : undefined
      }
    />
  );
}
